import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, FileText, Loader2, TrendingDown, TrendingUp, Tag, Camera } from "lucide-react";
import { formatCurrency } from "@/utils/formatters";
import { useCanSeeCost } from "@/hooks/useCanSeeCost";
import { SKIP_REASON_LABEL, type StockCheckSkippedItem, type StockCheckCadastroAviso } from "@/types/stockChecklist";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FinalizationStats {
  total_checked: number;
  total_adjustments: number;
  total_divergences: number;
  positive_divergences: number;
  negative_divergences: number;
  prices_updated: number;
  photos_updated: number;
  /** PROD-CUSTO-RLS (I.167): vazio para quem não tem a chave de preços (o banco não devolve o R$) */
  financial_impact: number | null;
}

interface StockChecklistSummaryDialogProps {
  open: boolean;
  stats: FinalizationStats;
  checklistName: string;
  /** A7a-i: itens contados que não foram acertados (motivo da regra do banco) */
  skipped?: StockCheckSkippedItem[];
  /** A7a-i: equipamentos acertados cuja contagem difere das unidades registradas */
  cadastroAvisos?: StockCheckCadastroAviso[];
  onDownloadPdf: () => Promise<void>;
  onClose: () => void;
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({
  value,
  label,
  icon: Icon,
  className = "",
}: {
  value: string | number;
  label: string;
  icon: React.ElementType;
  className?: string;
}) {
  return (
    <div className={`flex flex-col items-center justify-center gap-1 rounded-lg border p-4 ${className}`}>
      <Icon className="h-5 w-5 mb-1 opacity-70" />
      <span className="text-2xl font-bold font-mono">{value}</span>
      <span className="text-xs text-muted-foreground text-center">{label}</span>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export const StockChecklistSummaryDialog = ({
  open,
  stats,
  checklistName,
  skipped = [],
  cadastroAvisos = [],
  onDownloadPdf,
  onClose,
}: StockChecklistSummaryDialogProps) => {
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  // PROD-CUSTO-RLS (I.167, 3b): o impacto em R$ só para quem tem a chave de preços ou é master (regra única)
  const canSeeCost = useCanSeeCost();
  const impact = stats.financial_impact;

  const handleDownloadPdf = async () => {
    try {
      setIsPdfLoading(true);
      await onDownloadPdf();
    } finally {
      setIsPdfLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <CheckCircle2 className="h-8 w-8 text-green-500 shrink-0" />
            <div>
              <DialogTitle className="text-lg">Conferência Finalizada com Sucesso</DialogTitle>
              <DialogDescription className="text-sm mt-0.5 truncate max-w-[320px]">
                {checklistName}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Stats grid — 7 cards sempre visíveis; zero = esmaecido */}
        <div className="grid grid-cols-2 gap-3 my-2">
          <StatCard
            value={stats.total_checked}
            label="Produtos Conferidos"
            icon={CheckCircle2}
            className="bg-green-500/5 border-green-500/20 text-green-700 dark:text-green-400"
          />
          <StatCard
            value={stats.total_divergences}
            label="Com Divergência"
            icon={stats.total_divergences > 0 ? TrendingDown : CheckCircle2}
            className={
              stats.total_divergences > 0
                ? "bg-red-500/5 border-red-500/20 text-red-700 dark:text-red-400"
                : "bg-green-500/5 border-green-500/20 text-green-700 dark:text-green-400"
            }
          />
          <StatCard
            value={stats.positive_divergences > 0 ? `+${stats.positive_divergences}` : '0'}
            label="Divergências Positivas (Sobra)"
            icon={TrendingUp}
            className={
              stats.positive_divergences > 0
                ? "bg-blue-500/5 border-blue-500/20 text-blue-700 dark:text-blue-400"
                : "opacity-50"
            }
          />
          <StatCard
            value={stats.negative_divergences > 0 ? `-${stats.negative_divergences}` : '0'}
            label="Divergências Negativas (Falta)"
            icon={TrendingDown}
            className={
              stats.negative_divergences > 0
                ? "bg-red-500/5 border-red-500/20 text-red-700 dark:text-red-400"
                : "opacity-50"
            }
          />
          <StatCard
            value={stats.prices_updated}
            label="Preços Atualizados"
            icon={Tag}
            className={
              stats.prices_updated > 0
                ? "bg-amber-500/5 border-amber-500/20 text-amber-700 dark:text-amber-400"
                : "opacity-50"
            }
          />
          <StatCard
            value={stats.photos_updated}
            label="Fotos Inseridas"
            icon={Camera}
            className={
              stats.photos_updated > 0
                ? "bg-purple-500/5 border-purple-500/20 text-purple-700 dark:text-purple-400"
                : "opacity-50"
            }
          />
        </div>

        {/* Financial impact — PROD-CUSTO-RLS (I.167, 3b): só com a chave de preços e com o valor vindo do banco */}
        {canSeeCost && impact !== null && (
        <div className={`flex items-center justify-between rounded-lg border px-4 py-3 ${
          impact > 0
            ? "bg-amber-500/10 border-amber-500/20"
            : "bg-muted/30 border-muted opacity-60"
        }`}>
          <div>
            <p className={`text-sm font-medium ${impact > 0 ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"}`}>
              Impacto Financeiro
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {/* m-CONF.19: o número da finalização é o valor lançado no estoque */}
              Valor dos acertos lançados no estoque pela conferência
            </p>
          </div>
          <span className={`text-xl font-bold font-mono ${impact > 0 ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"}`}>
            {formatCurrency(impact)}
          </span>
        </div>
        )}

        {skipped.length > 0 && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm">
            <p className="font-medium text-amber-700 dark:text-amber-400">
              Não acertados ({skipped.length}) — estoque e data de contagem não mudaram
            </p>
            <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
              {skipped.map((s) => (
                <li key={s.product_id} className="text-xs">
                  <span className="font-medium">{s.name}</span>: {SKIP_REASON_LABEL[s.reason] ?? s.reason}
                  {" "}(contado {Number(s.counted)}, esperado {Number(s.expected)}
                  {s.registered != null ? `, registradas ${s.registered}` : ""})
                </li>
              ))}
            </ul>
          </div>
        )}

        {cadastroAvisos.length > 0 && (
          <div className="rounded-lg border px-4 py-3 text-sm">
            <p className="font-medium">Atualizar cadastro de unidades ({cadastroAvisos.length}) — acertados</p>
            <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto">
              {cadastroAvisos.map((a) => (
                <li key={a.product_id} className="text-xs">
                  <span className="font-medium">{a.name}</span>: contado {Number(a.counted)}, registradas {a.registered}
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter className="gap-2 mt-1">
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
          <Button onClick={handleDownloadPdf} disabled={isPdfLoading}>
            {isPdfLoading
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <FileText className="h-4 w-4 mr-2" />
            }
            Baixar PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
